<script setup>
import { ref, computed, watch } from 'vue';
import { Head, router, useForm, usePage } from '@inertiajs/vue3';
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Modal from '@pages/Shared/Modal.vue';
import Input from '@pages/Shared/form/Input.vue';
import Select from '@pages/Shared/form/Select.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Table from '@pages/Shared/ui/Table.vue';
import Spinner from '@pages/Shared/ui/Spinner.vue';
import { PhBank } from "@phosphor-icons/vue";
import { flashToast } from "@/toast.js";

const props = defineProps({
    classes:  Object,
    sections: Array,
    filters:  Object,
});

const openModal   = ref(false);
const modalStatus = ref(null);
const modalTitle  = ref('');
const showPrompt  = ref(false);

const filters = ref({ search: null, section: null, ...props.filters });

const form = useForm({
    _method: null,
    id:      null,
    name:    '',
    section: null,
});

const columns = [
    { key: 'index',        label: '#',       type: 'index'   },
    { key: 'name',         label: 'Name'                     },
    { key: 'section.name', label: 'Section'                  },
    { key: 'actions',      label: '',        type: 'actions' },
];

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true });

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    );
    router.get('/admin/classes', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const handleSearch = (value) => { filters.value.search = value; applyFilters(); };
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters(); };

const setFormData = (classRoom) => {
    form.id      = classRoom.id;
    form.name    = classRoom.name;
    form.section = classRoom.section?.id ?? null;
};

const editClass = (id) => {
    const classRoom = props.classes.data.find(c => c.id === id);
    if (!classRoom) return;
    setFormData(classRoom);
    modalTitle.value  = 'Edit Class';
    modalStatus.value = 'edit';
    openModal.value   = true;
};

const viewClass = (id) => {
    const classRoom = props.classes.data.find(c => c.id === id);
    if (!classRoom) return;
    setFormData(classRoom);
    modalTitle.value  = 'View Class';
    modalStatus.value = 'view';
    openModal.value   = true;
};

const deleteClass = (id) => { form.id = id; showPrompt.value = true; };

const confirmDelete = () => {
    form.delete(`/admin/classes/${form.id}`, {
        onSuccess: (page) => {
            showPrompt.value = false;
            form.clearErrors();
            form.reset();
            flashToast(page.props.flash);
        }
    });
};

const openAddClassModal = () => {
    form.reset();
    form.clearErrors();
    modalTitle.value  = 'Add Class';
    modalStatus.value = 'create';
    openModal.value   = true;
};

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            form.clearErrors();
            form.reset();
            openModal.value = false;
            flashToast(page.props.flash);
        }
    };
    if (modalStatus.value === 'edit') {
        form._method = 'put';
        form.post(`/admin/classes/${form.id}`, options);
    } else {
        form.post('/admin/classes', options);
    }
};

const handleModalClose = () => {
    modalStatus.value = null;
    modalTitle.value  = null;
    openModal.value   = false;
    form.clearErrors();
    form.reset();
};

const page = usePage();
const can  = computed(() => page.props.can || {});
</script>

<template>
    <Layout>
        <Head title="Classes Management" />

        <Prompt
            title="class"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

                <Input v-model="form.name" type="text" label="Name" placeholder="Class Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.name" class="mb-4" />

                <Select v-model="form.section" label="Section"
                    :options="sections.map(s => ({ label: s.name, value: s.id }))"
                    placeholder="Select section" :disabled="modalStatus === 'view'"
                    :error="form.errors.section" class="mb-4" />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                           transition flex justify-center items-center gap-2"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Class' : 'Add Class' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Classes</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all classes</p>
            </div>
            <button
                v-if="can.createClass"
                @click="openAddClassModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhBank :size="20" />
                Add Class
            </button>
        </div>

        <Table
            :data="classes"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: [
                        { label: 'All Sections', value: null },
                        ...sections.map(s => ({ label: s.name, value: s.id })),
                    ],
                    value: filters.section,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @edit="editClass"
            @view="viewClass"
            @delete="deleteClass"
        />
    </Layout>
</template>